import {useMutation, useQueryClient} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {storeMutationAdapter} from '../../../../../../adapter/storeMutationAdapter';
import {STORE_QUERY_KEY_MAP} from '../../../../../../api/store/constant';
import {OnlyInLoginChecker} from '../../../../../../common/checkers/OnlyInLoginChecker';
import {getCurrentUrlWithAdditonalParam} from '../../../../../../common/getCurrentUrlWithAdditonalParam';
import {t, tt} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {ApiStatusCodeVo as NicolasApiStatusVo} from '../../../../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {TRIGGER_COMMAND_KEY} from '../../../../../../common/shared/constants';
import {
  COMMAND_KEY,
  useCommandWithLocalStorage,
} from '../../../../../../common/shared/hooks/useCommandWithLocalStorage';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {ButtonV2Variant} from '../../../../../../design-system/atoms/ButtonV2/ButtonV2.enum';
import {DialogProps} from '../../../../../../design-system/organisms/Dialog/DialogProps.interface';
import {useDialog} from '../../../../../../dialog/useDialog.hook';
import {commonErrorHandler} from '../../../../../../errors/commonErrorHandler';
import {showToast} from '../../../../../../toast/toastHandler';
import {useLogin} from '../../../../../../user/useLogin.hook';
import {generateRelayRedirectUrl} from '../../../../../relay/redirect/util/generateRelayRedirectUrl';
import {VIEWER_TOAST_MESSAGES} from '../constant/viewerConstant';
import {MyRatingFetcherHeadless} from '../headless/MyRatingFetcherHeadless';
import {setMyRating} from '../state/viewerEndSlice';
import {updateCacheAddRating} from '../util/updateCacheAddRating';
import {updateCacheSubtractRating} from '../util/updateCacheSubtractRating';

export function useRatingDialog({
  seriesId,
  productId,
}: {
  seriesId: number;
  productId: number;
}) {
  const dispatch = useTypedDispatch();
  const {isLogin, doLogin} = useLogin();
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const myRating = useTypedSelector(
    state => state.viewerEnd.myRating[productId]
  );
  const queryClient = useQueryClient();

  const {saveCommand, runCommand} = useCommandWithLocalStorage();
  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });

  const {mutate} = useMutation(storeMutationAdapter.setMyRating());

  // 별점 저장하기
  const onClickMyRatingConfirm = useCallback(
    ({
      rating,
      fromSavedCommand,
    }: {
      rating: number;
      fromSavedCommand?: boolean;
    }) => {
      if (!seriesId || !productId || !rating) {
        return;
      }
      mutate(
        {
          setMyRatingInput: {
            seriesId,
            productId,
            rating,
          },
        },
        {
          onSuccess: (result, variables) => {
            if (result.setMyRating.status !== 'Success') {
              return;
            }
            queryClient.invalidateQueries({
              queryKey: STORE_QUERY_KEY_MAP.myRating({
                seriesId,
                productId,
              }),
            });
            const message = myRating
              ? VIEWER_TOAST_MESSAGES.ratingModify
              : VIEWER_TOAST_MESSAGES.ratingConfirm;

            const oldRating = myRating;
            const newRating = variables.setMyRatingInput.rating;
            if (!isNil(oldRating)) {
              updateCacheSubtractRating({
                seriesId,
                productId,
                myRating: oldRating,
              });
            }
            updateCacheAddRating({
              seriesId,
              productId,
              myRating: newRating,
            });
            dispatch(setMyRating({productId, myRating: newRating}));
            hideDialog();
            showToast({
              message: tt(message),
              isMobile: requestContext.requestInfo.display.isMobile,
            });
          },
          onError: (error, variables) => {
            if (
              error.serializedError.resultCode ===
                NicolasApiStatusVo.NotAuthorized ||
              error.serializedError.resultCode === NicolasApiStatusVo.KakaoError
            ) {
              if (!fromSavedCommand) {
                saveCommand({
                  commandKey: COMMAND_KEY.CLICK_STAR,
                  command: {
                    rating: variables.setMyRatingInput.rating,
                    location: window.location.pathname,
                  },
                });
              }
            }
            commonErrorHandler(error.serializedError);
          },
        }
      );
    },
    [
      mutate,
      productId,
      saveCommand,
      seriesId,
      myRating,
      dispatch,
      hideDialog,
      queryClient.invalidateQueries,
    ]
  );

  useEffect(() => {
    if (!isNil(myRating)) {
      runCommand({
        commandKey: COMMAND_KEY.CLICK_STAR,
        action: async commandData => {
          const {rating} = commandData;
          await onClickMyRatingConfirm({rating, fromSavedCommand: true});
        },
      });
    }
  }, [onClickMyRatingConfirm, runCommand, myRating]);

  // 별점 모달 띄우기
  const onClickStar = useCallback(
    (parameters?: {fromSavedCommand?: boolean}) => {
      if (!isLogin) {
        if (!parameters?.fromSavedCommand) {
          saveCommand({
            commandKey: COMMAND_KEY.CLICK_STAR_POPUP,
            command: {location: window.location.pathname},
          }).then(() =>
            doLogin(
              generateRelayRedirectUrl(
                getCurrentUrlWithAdditonalParam(
                  TRIGGER_COMMAND_KEY,
                  COMMAND_KEY.CLICK_STAR_POPUP
                )
              )
            )
          );
        }
        return;
      }
      if (isWebView) {
        window?.KPApp?.onClickRating?.();
        return;
      }
      showDialog();
    },
    [doLogin, isLogin, isWebView, saveCommand, showDialog]
  );
  useEffect(() => {
    runCommand({
      commandKey: COMMAND_KEY.CLICK_STAR_POPUP,
      action: async () => {
        await onClickStar({fromSavedCommand: true});
      },
    });
  }, [onClickStar, runCommand]);

  const RatingDialog = useMemo(() => {
    const _RatingDialog: React.FC<Omit<DialogProps, 'isOpen' | 'onClose'>> = ({
      children,
      ...props
    }) => {
      // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
      const [rating, setRating] = useState<number>(myRating ?? 10);
      // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
      const onChangeRating = useCallback((newValue: number) => {
        setRating(newValue);
      }, []);
      return (
        <>
          <OnlyInLoginChecker>
            <MyRatingFetcherHeadless
              seriesId={seriesId}
              productId={productId}
            />
          </OnlyInLoginChecker>
          <DialogWrapper onClick={stopPropagationEvent} {...props}>
            <Dialog.Dimmed />
            <Dialog.Title>{t('viewer_end_rating_popup_title')}</Dialog.Title>
            <Dialog.List>
              <Dialog.Star rating={rating} onChange={onChangeRating} />
              <Dialog.Label className="font-small1 text-theme-solid-60">
                {t('viewer_end_rating_popup_info')}
              </Dialog.Label>
            </Dialog.List>
            <Dialog.BottomCombination>
              <Dialog.Button onClick={hideDialog}>
                {t('common_cancel_button')}
              </Dialog.Button>
              <Dialog.Button
                onClick={() => onClickMyRatingConfirm({rating})}
                variant={ButtonV2Variant.Primary}
              >
                <span id="별점 모달">
                  {myRating
                    ? t('viewer_end_rating_modify_button')
                    : t('viewer_end_rating_complete_button')}
                </span>
                {children}
              </Dialog.Button>
            </Dialog.BottomCombination>
          </DialogWrapper>
        </>
      );
    };
    return _RatingDialog;
  }, [
    Dialog,
    DialogWrapper,
    hideDialog,
    myRating,
    onClickMyRatingConfirm,
    productId,
    seriesId,
  ]);

  return {
    RatingDialog,
    onClickStar,
  };
}
